/**
 * Elevate experience contract v1 (#1465 / ADR F-29).
 *
 * This is the shared semantic boundary between host-native renderers. It does
 * not prescribe Fluent, Solum components, transport, credentials or pixels.
 * An adapter maps its already-authorised source into this shape and executes
 * every action at that item's source of truth.
 *
 * Reference measurements:
 *   - D365 Elevate ee4c61ee (2026-08-23)
 *   - Marble b7533ae9 (2026-08-24)
 *
 * Deliberately NOT copied from the D365 reference: server re-query per search
 * term, tab-switch filter loss, hidden email-preview leakage, unnamed calendar
 * controls, or host-only Xrm session mechanics. A reference is evidence, not a
 * licence to preserve its defects.
 */

export const ELEVATE_CONTRACT_VERSION = '1.0.0' as const

/**
 * The directions the activity schema constrains today.
 *
 * `modules/activity/migrations/0001_activity.sql` carries
 * `check (direction in ('inbound','outbound','internal'))`, and
 * `elevateDirections.seam.test.ts` reads that constraint and fails if this list
 * stops matching it. A hand-maintained copy of a database's vocabulary agrees
 * with it right up until somebody widens the constraint, and then the surface
 * quietly stops labelling a direction that exists.
 *
 * This is a LABEL table, not an allow-list: `elevateDirectionLabel` renders an
 * unknown direction rather than hiding the row.
 */
export const ELEVATE_DIRECTIONS = Object.freeze({
  inbound: 'Inbound',
  outbound: 'Outbound',
  internal: 'Internal',
} as Record<string, string>)

/** A direction's label, or the value itself when the schema has moved on. */
export function elevateDirectionLabel(direction: string): string {
  const known = ELEVATE_DIRECTIONS[direction.toLowerCase()]
  if (known) return known
  const raw = direction.replaceAll('_', ' ').trim()
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : direction
}

export const ELEVATE_CAPABILITIES = [
  'scope.record',
  'scope.mine',
  'scope.teams',
  'scope.queues',
  'card.provenance',
  'card.metadata',
  'card.progressive-disclosure',
  'card.capability-actions',
  'organise.search',
  'organise.filters',
  'organise.sort',
  'organise.group',
  'organise.paged-loading',
  'selection.batch-actions',
  'activity.quick-create',
  'activity.calendar',
  'activity.threads',
  'activity.attachments',
  'activity.audit',
  'activity.pin-read',
  'activity.reminders',
  'activity.sla',
  'preferences.user',
  'accessibility.keyboard',
  'state.partial-source',
  'source.interleave',
] as const

export type ElevateCapability = typeof ELEVATE_CAPABILITIES[number]
export type ElevateCapabilityState = 'live' | 'partial' | 'deferred' | 'unsupported'

export interface ElevateCapabilityEvidence {
  capability: ElevateCapability
  state: ElevateCapabilityState
  evidence: string
}

export interface ElevateHostProfile {
  host: string
  revision: string
  measuredAt: string
  /** Exact files/documents inspected at revision; never an untraceable claim. */
  sourcePaths: readonly string[]
  capabilities: readonly ElevateCapabilityEvidence[]
}

export type ElevateSourceKind = 'dataverse' | 'marble' | 'other'

export interface ElevateSource {
  /** Stable deployment/source id, used for ordering and action routing. */
  id: string
  label: string
  kind: ElevateSourceKind
  /** Source-of-truth revision or row version observed by the adapter. */
  revision: string
}

export type ElevateScope =
  | { kind: 'record'; entity: string; recordId: string }
  | { kind: 'mine' }
  | { kind: 'team'; teamId: string }
  | { kind: 'queue'; queueId: string }

export interface ElevateActor {
  id: string
  label: string
  role: 'actor' | 'owner' | 'assignee' | 'participant'
  kind?: 'person' | 'team' | 'queue' | 'organisation'
}

export interface ElevateRecordRef {
  entity: string
  id: string
  label?: string
}

export interface ElevateProvenance extends ElevateRecordRef {
  /** Host-native destination. Absence is an explicit refusal, never null. */
  destination:
    | { state: 'available'; deepLink: string }
    | { state: 'refused'; reason: string }
}

export interface ElevateTime {
  /** Required deterministic ordering key, normally occurredAt or dueAt. */
  effectiveAt: string
  occurredAt?: string
  dueAt?: string
  startsAt?: string
  endsAt?: string
  timeZone?: string
}

export interface ElevateSlaSignal {
  state: 'on-track' | 'at-risk' | 'breached' | 'paused'
  label: string
  dueAt?: string
}

export type ElevateBatchPolicy = 'single' | 'same-source' | 'source-split'

interface ElevateActionBase {
  /** Stable semantic id. The translated label may change; this may not. */
  id: string
  label: string
  /** Must equal the containing item's source.id. */
  sourceId: string
  requiresReason?: boolean
}

export type ElevateAction = ElevateActionBase & (
  | { state: 'available'; batch: ElevateBatchPolicy }
  | { state: 'refused'; reason: string; refresh: 'item' }
)

export interface ElevateItem {
  contractVersion: typeof ELEVATE_CONTRACT_VERSION
  id: string
  source: ElevateSource
  provenance: ElevateProvenance
  kind: string
  channel?: string
  title: string
  summary?: string
  /**
   * The governed body AS THE DOOR RETURNED IT, and whether the door redacted it.
   *
   * `activity.contextual_timeline` is the redaction boundary: a row visible only
   * through a linked person comes back with `body` replaced by `[redacted]` and
   * `body_redacted` true. Both halves are needed. The text alone would print
   * `[redacted]` as though it were what somebody wrote; the flag alone would
   * lose the content that IS available on a row directly about this record.
   *
   * Kept apart from `summary` because they are different claims: a summary is
   * the adapter's own gloss, and this is the product's content under governance.
   */
  body?: { text: string; redacted: boolean }
  /**
   * Which way it went, in the door's own vocabulary — never re-derived here.
   * See `ELEVATE_DIRECTIONS` for the set the schema constrains today; an
   * unrecognised value is rendered rather than dropped, because a module that
   * widens the constraint is not a reason to stop telling the truth about a row.
   */
  direction?: string
  time: ElevateTime
  actors: readonly ElevateActor[]
  related: readonly ElevateRecordRef[]
  status: { id: string; label: string }
  priority?: { id: string; label: string }
  unread?: boolean
  pinned?: boolean
  tags?: readonly string[]
  sla?: ElevateSlaSignal
  affordances?: {
    attachmentCount?: number
    threadCount?: number
    auditCount?: number
  }
  actions: readonly ElevateAction[]
}

export type ElevateSourceFeed =
  | { source: ElevateSource; state: 'ready'; items: readonly ElevateItem[] }
  | { source: ElevateSource; state: 'error'; message: string }

export type ElevateCombinedFeed =
  | { state: 'ready'; items: readonly ElevateItem[]; unavailable: readonly ElevateSource[] }
  | { state: 'partial'; items: readonly ElevateItem[]; unavailable: readonly ElevateSource[] }
  | { state: 'error'; items: readonly []; unavailable: readonly ElevateSource[] }

export type ElevateActionOutcome =
  | { state: 'succeeded'; sourceId: string; revision: string; evidenceId?: string }
  | { state: 'refused'; sourceId: string; reason: string; refresh: 'item' }

export interface ElevateBatchOperation {
  sourceId: string
  actionId: string
  itemIds: readonly string[]
}

export type ElevateBatchPlan =
  | { state: 'ready'; split: boolean; operations: readonly ElevateBatchOperation[] }
  | { state: 'refused'; reasons: readonly string[] }

/** Newest first, then stable source/id ties. Host array order is never meaning. */
export function compareElevateItems(a: ElevateItem, b: ElevateItem): number {
  const byTime = b.time.effectiveAt.localeCompare(a.time.effectiveAt)
  if (byTime !== 0) return byTime
  const bySource = a.source.id.localeCompare(b.source.id)
  return bySource !== 0 ? bySource : a.id.localeCompare(b.id)
}

/**
 * The behavioral identity shared by both renderers. Host-native deep links,
 * translated labels and visual choices are intentionally outside this value.
 */
export function elevateSemanticSnapshot(item: ElevateItem) {
  return {
    sourceId: item.source.id,
    record: { entity: item.provenance.entity, id: item.provenance.id },
    kind: item.kind,
    channel: item.channel,
    title: item.title,
    summary: item.summary,
    time: item.time,
    actors: [...item.actors]
      .map(({ id, role, kind }) => ({ id, role, kind }))
      .sort((a, b) => `${a.role}:${a.id}`.localeCompare(`${b.role}:${b.id}`)),
    related: [...item.related]
      .map(({ entity, id }) => ({ entity, id }))
      .sort((a, b) => `${a.entity}:${a.id}`.localeCompare(`${b.entity}:${b.id}`)),
    status: item.status.id,
    priority: item.priority?.id,
    unread: item.unread,
    pinned: item.pinned,
    sla: item.sla && { state: item.sla.state, dueAt: item.sla.dueAt },
    actions: [...item.actions]
      .map((action) => ({
        id: action.id,
        state: action.state,
        batch: action.state === 'available' ? action.batch : undefined,
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  }
}

/** Keep healthy sources useful and name every failed source explicitly. */
export function combineElevateFeeds(feeds: readonly ElevateSourceFeed[]): ElevateCombinedFeed {
  const available = feeds.filter((feed): feed is Extract<ElevateSourceFeed, { state: 'ready' }> => feed.state === 'ready')
  for (const feed of available) {
    const misplaced = feed.items.find((item) => item.source.id !== feed.source.id)
    if (misplaced) throw new Error(`Item '${misplaced.id}' was returned by the wrong source feed.`)
  }
  const unavailable = feeds.filter((feed) => feed.state === 'error').map((feed) => feed.source)
  const items = available.flatMap((feed) => feed.items).sort(compareElevateItems)
  if (available.length === 0) return { state: 'error', items: [], unavailable }
  return { state: unavailable.length > 0 ? 'partial' : 'ready', items, unavailable }
}

/**
 * Build explicit source-bound operations. A mixed-source selection either
 * opts into source splitting on every item or is refused; it can never become
 * one ambient write under whichever credential happens to be available.
 */
export function planElevateBatch(items: readonly ElevateItem[], actionId: string): ElevateBatchPlan {
  if (items.length === 0) return { state: 'refused', reasons: ['No items are selected.'] }

  const selected = items.map((item) => ({ item, action: item.actions.find((action) => action.id === actionId) }))
  const reasons: string[] = []
  for (const { item, action } of selected) {
    if (!action) reasons.push(`${item.id}: action '${actionId}' is not offered.`)
    else if (action.sourceId !== item.source.id) reasons.push(`${item.id}: action source does not match item source.`)
    else if (action.state === 'refused') reasons.push(`${item.id}: ${action.reason}`)
  }
  if (reasons.length > 0) return { state: 'refused', reasons }

  const available = selected as { item: ElevateItem; action: Extract<ElevateAction, { state: 'available' }> }[]
  if (available.length > 1 && available.some(({ action }) => action.batch === 'single')) {
    return { state: 'refused', reasons: [`Action '${actionId}' is single-item only.`] }
  }

  const sourceIds = [...new Set(available.map(({ item }) => item.source.id))].sort()
  if (sourceIds.length > 1 && available.some(({ action }) => action.batch !== 'source-split')) {
    return { state: 'refused', reasons: [`Action '${actionId}' cannot span sources.`] }
  }

  const operations = sourceIds.map((sourceId) => ({
    sourceId,
    actionId,
    itemIds: available.filter(({ item }) => item.source.id === sourceId).map(({ item }) => item.id),
  }))
  return { state: 'ready', split: operations.length > 1, operations }
}

/** Refusal after load is not a failed success: refresh capabilities, no receipt. */
export function reconcileElevateAction(outcome: ElevateActionOutcome): {
  refreshItem: true
  receipt?: { sourceId: string; revision: string; evidenceId?: string }
  refusal?: string
} {
  if (outcome.state === 'refused') return { refreshItem: true, refusal: outcome.reason }
  return {
    refreshItem: true,
    receipt: { sourceId: outcome.sourceId, revision: outcome.revision, evidenceId: outcome.evidenceId },
  }
}

/** Structural problems a host conformance fixture must reject before release. */
export function elevateItemIssues(item: ElevateItem): string[] {
  const issues: string[] = []
  if (item.contractVersion !== ELEVATE_CONTRACT_VERSION) issues.push('contract version is not supported')
  if (!item.id.trim()) issues.push('item id is empty')
  if (!item.source.id.trim() || !item.source.revision.trim()) issues.push('source provenance is incomplete')
  if (!item.provenance.entity.trim() || !item.provenance.id.trim()) issues.push('record provenance is incomplete')
  if (item.provenance.destination.state === 'available' && !item.provenance.destination.deepLink.trim()) {
    issues.push('available destination has no deep link')
  }
  if (item.provenance.destination.state === 'refused' && !item.provenance.destination.reason.trim()) {
    issues.push('refused destination has no reason')
  }
  if (!item.time.effectiveAt.trim()) issues.push('effective ordering time is empty')
  const ids = new Set<string>()
  for (const action of item.actions) {
    if (ids.has(action.id)) issues.push(`action '${action.id}' is duplicated`)
    ids.add(action.id)
    if (action.sourceId !== item.source.id) issues.push(`action '${action.id}' routes away from its source of truth`)
    if (action.state === 'refused' && !action.reason.trim()) issues.push(`action '${action.id}' has an empty refusal reason`)
  }
  return issues
}

/**
 * An adapter DECLARES what its host can execute. Every action outside the
 * declared set becomes a visible refusal carrying a reason, because an absent
 * capability that the client infers is a fiction — the item still says the act
 * exists and the button still fails when a person presses it.
 *
 * This is adapter-side, deliberately: only the thing that read the source knows
 * which governed verbs it can route. The renderer checks; it never decides.
 */
export function declareElevateSupport(
  item: ElevateItem,
  supported: readonly string[],
  reason: string,
): ElevateItem {
  if (!reason.trim()) throw new Error('A declared absence needs a reason a person can read.')
  const permitted = new Set(supported)
  return {
    ...item,
    actions: item.actions.map((action) => action.state === 'available' && !permitted.has(action.id)
      ? { id: action.id, label: action.label, sourceId: action.sourceId, requiresReason: action.requiresReason, state: 'refused' as const, reason, refresh: 'item' as const }
      : action),
  }
}

/**
 * Actions this item offers as available that the host never declared it can
 * execute. Non-empty means the adapter and the renderer disagree, which is a
 * button that would fail on press — a conformance defect, not a runtime error
 * to discover from a user.
 */
export function elevateUnsupportedActions(item: ElevateItem, supported: readonly string[]): string[] {
  const permitted = new Set(supported)
  return item.actions
    .filter((action) => action.state === 'available' && !permitted.has(action.id))
    .map((action) => action.id)
}

/**
 * The complete check a renderer owes before it draws an item: structural
 * conformance plus the host's own declared reach. `supported` omitted means the
 * host makes no reach claim, and only structure is checked.
 */
export function elevateRenderIssues(item: ElevateItem, supported?: readonly string[]): string[] {
  const issues = elevateItemIssues(item)
  if (supported === undefined) return issues
  for (const id of elevateUnsupportedActions(item, supported)) {
    issues.push(`action '${id}' is offered but this host declared no way to execute it`)
  }
  return issues
}

export function elevateProfileIssues(profile: ElevateHostProfile): string[] {
  const counts = new Map<ElevateCapability, number>()
  for (const row of profile.capabilities) counts.set(row.capability, (counts.get(row.capability) ?? 0) + 1)
  const issues = ELEVATE_CAPABILITIES.flatMap((capability) => {
    const count = counts.get(capability) ?? 0
    if (count === 0) return [`${profile.host}: '${capability}' is unmeasured`]
    return count === 1 ? [] : [`${profile.host}: '${capability}' is measured ${count} times`]
  })
  if (!profile.revision.trim()) issues.push(`${profile.host}: reference revision is empty`)
  if (profile.sourcePaths.length === 0) issues.push(`${profile.host}: reference source paths are empty`)
  for (const row of profile.capabilities) {
    if (!row.evidence.trim()) issues.push(`${profile.host}: '${row.capability}' has no evidence`)
  }
  return issues
}
